import React, { useEffect, useRef, useState } from 'react';
import { bool, func, object } from 'prop-types';
import classNames from 'classnames';

import { FormattedMessage, injectIntl } from '../../../util/reactIntl';
import { Icons } from '../../../components';

import { shortSortLabel } from './filterBarHelpers';

import css from './BrowseSortMenu.module.css';

/**
 * Sort control for the filter row (ledger "Filter system — FINAL 2026-08-25"):
 * button shows "Sort: {short label} ▾" ("Newest", "High to low"); the menu
 * keeps full labels with a check + quiet tint on the selected row.
 */
const BrowseSortMenuComponent = props => {
  const { sortConfig, urlQueryParams, onSortBy, hasKeywords, intl } = props;
  const [isOpen, setIsOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onDown = e => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setIsOpen(false);
    };
    const onKey = e => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [isOpen]);

  if (!sortConfig?.active) return null;

  const queryParamName = sortConfig.queryParamName || 'sort';
  // Relevance is keyword-only (ledger: "Relevance = keyword only"); with
  // keywords and no sort param the API sorts by relevance, so it's also the
  // default there. Selecting it clears the sort param rather than sending it.
  const relevanceKey = sortConfig.relevanceKey;
  const options = (sortConfig.options || []).filter(o => hasKeywords || o.key !== relevanceKey);
  const defaultKey = hasKeywords && relevanceKey ? relevanceKey : options[0]?.key;
  const currentValue = urlQueryParams[queryParamName] || defaultKey;
  const labelOf = option =>
    option.labelTranslationKey ? intl.formatMessage({ id: option.labelTranslationKey }) : option.label || option.key;
  const current = options.find(o => o.key === currentValue) || options[0];

  return (
    <span className={css.root} ref={rootRef}>
      <button type="button" className={classNames(css.button, { [css.buttonOpen]: isOpen })} onClick={() => setIsOpen(o => !o)}>
        <FormattedMessage id="BrowseSortMenu.buttonLabel" values={{ label: shortSortLabel(labelOf(current)) }} />
        <span className={classNames(css.caret, { [css.caretOpen]: isOpen })}>
          <Icons name="downAngle" />
        </span>
      </button>
      {isOpen ? (
        <div className={css.menu}>
          {options.map(option => {
            const isSelected = option.key === currentValue;
            return (
              <button
                type="button"
                key={option.key}
                className={classNames(css.optionRow, { [css.optionSelected]: isSelected })}
                onClick={() => {
                  onSortBy(queryParamName, option.key === relevanceKey ? null : option.key);
                  setIsOpen(false);
                }}
              >
                {/* Check sits LEFT in a fixed gutter (prototype; matches the
                    phone sort sheet) */}
                <span className={css.optionCheck}>{isSelected ? '✓' : ''}</span>
                <span className={css.optionLabel}>{labelOf(option)}</span>
              </button>
            );
          })}
        </div>
      ) : null}
    </span>
  );
};

BrowseSortMenuComponent.defaultProps = {
  hasKeywords: false,
};

BrowseSortMenuComponent.propTypes = {
  sortConfig: object.isRequired,
  urlQueryParams: object.isRequired,
  onSortBy: func.isRequired,
  hasKeywords: bool,
};

export default injectIntl(BrowseSortMenuComponent);
